import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    PaperPlaneTilt,
    CheckCircle,
    UsersThree,
    Info,
    MagnifyingGlass,
} from "@phosphor-icons/react";
import { submitCommunityChallenge, getTopPredictions } from "../lib/api";

const CHALLENGE_TYPES = [
    { value: "regular", label: "Regular" },
    { value: "wildcard", label: "Wildcard" },
    { value: "captain", label: "Captain" },
    { value: "special", label: "Special" },
];

const DURATIONS = [
    { value: "single_gw", label: "Single GW" },
    { value: "double_gw", label: "Double GW" },
    { value: "triple_gw", label: "Triple GW" },
    { value: "season_long", label: "Season Long" },
];

export default function Submit() {
    const [title, setTitle] = useState("");
    const [shortDesc, setShortDesc] = useState("");
    const [description, setDescription] = useState("");
    const [challengeType, setChallengeType] = useState("regular");
    const [duration, setDuration] = useState("single_gw");
    const [hint, setHint] = useState("");
    const [submitterName, setSubmitterName] = useState("");
    const [submitterEmail, setSubmitterEmail] = useState("");
    const [selectedPlayers, setSelectedPlayers] = useState([]); // FPL ids
    const [playerSearch, setPlayerSearch] = useState("");
    const [pool, setPool] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // created challenge

    useEffect(() => {
        getTopPredictions(5, 200)
            .then((d) => setPool(d.predictions || []))
            .catch(() => toast.error("Failed to load player pool"));
    }, []);

    const filteredPool = useMemo(() => {
        const q = playerSearch.trim().toLowerCase();
        if (!q) return pool.slice(0, 30);
        return pool
            .filter((p) =>
                [p.name, p.team, p.position]
                    .map((s) => (s || "").toLowerCase())
                    .some((s) => s.includes(q)),
            )
            .slice(0, 30);
    }, [pool, playerSearch]);

    const togglePlayer = (id) => {
        setSelectedPlayers((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 6) {
                toast.error("Max 6 players per challenge");
                return prev;
            }
            return [...prev, id];
        });
    };

    const canSubmit =
        title.trim().length >= 3 &&
        description.trim().length >= 10 &&
        submitterName.trim().length >= 1 &&
        !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const created = await submitCommunityChallenge({
                title: title.trim(),
                shortDescription: shortDesc.trim(),
                descriptionHtml: description.trim(),
                challengeType,
                duration,
                hint: hint.trim(),
                playerIds: selectedPlayers,
                submitterName: submitterName.trim(),
                submitterEmail: submitterEmail.trim(),
            });
            setSubmitted(created);
            toast.success("Challenge submitted for moderator review!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setSubmitted(null);
        setTitle("");
        setShortDesc("");
        setDescription("");
        setHint("");
        setSelectedPlayers([]);
        setPlayerSearch("");
    };

    return (
        <AppShell>
            <div className="space-y-6 max-w-4xl" data-testid="submit-page">
                {/* Header */}
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                        Community · Submit Challenge
                    </div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                        Pitch a Challenge
                    </h1>
                    <p className="text-white/50 text-sm mt-1 max-w-2xl">
                        Write your own fantasy challenge idea. Once submitted, a moderator will
                        review it and either approve for publishing or send it back with notes.
                    </p>
                </div>

                {submitted ? (
                    <SuccessCard submitted={submitted} onReset={handleReset} />
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="submit-form">
                        {/* Title */}
                        <Field label="Title *">
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. The Haaland Gambit"
                                maxLength={120}
                                required
                                data-testid="submit-title"
                                className="bg-black/40 border-white/10 rounded-none font-mono text-sm"
                            />
                        </Field>

                        {/* Short description */}
                        <Field label="Short description" hint="One-line teaser shown in library cards (max 240 chars)">
                            <Input
                                value={shortDesc}
                                onChange={(e) => setShortDesc(e.target.value)}
                                placeholder="e.g. Dare to captain the cheapest forward?"
                                maxLength={240}
                                data-testid="submit-short"
                                className="bg-black/40 border-white/10 rounded-none font-mono text-sm"
                            />
                        </Field>

                        {/* Full description */}
                        <Field label="Full description *" hint="HTML allowed. Describe rules, context, what makes this fun.">
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="<p>Captain a forward who costs under £6.0m this gameweek. Bonus points if they score in both fixtures of a DGW.</p>"
                                rows={5}
                                required
                                data-testid="submit-description"
                                className="w-full bg-black/40 border border-white/10 rounded-none font-mono text-sm px-3 py-2 focus:outline-none focus:border-fa-yellow"
                            />
                        </Field>

                        {/* Type + Duration */}
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Challenge type">
                                <select
                                    value={challengeType}
                                    onChange={(e) => setChallengeType(e.target.value)}
                                    data-testid="submit-type"
                                    className="bg-black/40 border border-white/10 text-white font-mono text-sm h-10 px-3 w-full"
                                >
                                    {CHALLENGE_TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Duration">
                                <select
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    data-testid="submit-duration"
                                    className="bg-black/40 border border-white/10 text-white font-mono text-sm h-10 px-3 w-full"
                                >
                                    {DURATIONS.map((d) => (
                                        <option key={d.value} value={d.value}>{d.label}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        {/* Hint */}
                        <Field label="Hint" hint="Optional tip shown during the challenge (max 500 chars)">
                            <Input
                                value={hint}
                                onChange={(e) => setHint(e.target.value)}
                                placeholder="e.g. Look at fixtures vs weak defences"
                                maxLength={500}
                                data-testid="submit-hint"
                                className="bg-black/40 border-white/10 rounded-none font-mono text-sm"
                            />
                        </Field>

                        {/* Players */}
                        <PlayerPicker
                            pool={filteredPool}
                            search={playerSearch}
                            setSearch={setPlayerSearch}
                            selected={selectedPlayers}
                            onToggle={togglePlayer}
                        />

                        {/* Submitter info */}
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/10">
                            <Field label="Your name *">
                                <Input
                                    value={submitterName}
                                    onChange={(e) => setSubmitterName(e.target.value)}
                                    placeholder="e.g. AleshaJRK"
                                    maxLength={60}
                                    required
                                    data-testid="submit-name"
                                    className="bg-black/40 border-white/10 rounded-none font-mono text-sm"
                                />
                            </Field>
                            <Field label="Email (optional)" hint="Only used for moderator follow-up">
                                <Input
                                    type="email"
                                    value={submitterEmail}
                                    onChange={(e) => setSubmitterEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    maxLength={120}
                                    data-testid="submit-email"
                                    className="bg-black/40 border-white/10 rounded-none font-mono text-sm"
                                />
                            </Field>
                        </div>

                        {/* Submit */}
                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="submit-challenge-btn"
                                className="bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-sm uppercase h-11 px-8"
                            >
                                <PaperPlaneTilt size={14} weight="fill" className="mr-2" />
                                {submitting ? "Submitting…" : "Submit for review"}
                            </Button>
                            <div className="flex items-center gap-2 text-white/40 font-mono text-[11px]">
                                <Info size={12} weight="bold" />
                                No account required — just give us a name so we know who to credit.
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </AppShell>
    );
}

// -- sub-components ---------------------------------------------------------

function Field({ label, hint, children }) {
    return (
        <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-1">
                {label}
            </Label>
            {children}
            {hint && <div className="font-mono text-[10px] text-white/30 mt-1">{hint}</div>}
        </div>
    );
}

function PlayerPicker({ pool, search, setSearch, selected, onToggle }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Relevant players <span className="text-white/30">(optional, max 6)</span>
                </Label>
                <span className="font-mono text-[10px] text-fa-yellow" data-testid="selected-count">
                    {selected.length} / 6 selected
                </span>
            </div>
            <div className="relative mb-2">
                <MagnifyingGlass
                    size={14}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, team or position…"
                    data-testid="submit-player-search"
                    className="pl-9 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                />
            </div>
            <div className="border border-white/10 max-h-64 overflow-y-auto" data-testid="player-pool">
                {pool.length === 0 ? (
                    <div className="font-mono text-xs text-white/40 py-4 text-center">
                        No players match the search.
                    </div>
                ) : (
                    pool.map((p) => {
                        const on = selected.includes(p.id);
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => onToggle(p.id)}
                                data-testid={`pool-player-${p.id}`}
                                className={`w-full flex items-center justify-between px-3 py-2 border-b border-white/5 transition-colors ${on ? "bg-fa-yellow/10 border-l-2 border-l-fa-yellow" : "hover:bg-white/5"
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    {on && <CheckCircle size={14} weight="fill" className="text-fa-yellow" />}
                                    <div className="text-left">
                                        <div className="font-display font-bold text-sm">{p.name}</div>
                                        <div className="font-mono text-[10px] text-white/50">
                                            {p.team} · {p.position} · £{p.price?.toFixed?.(1)}m · {p.predicted_points_total?.toFixed?.(1)}pts
                                        </div>
                                    </div>
                                </div>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">
                                    FPL #{p.id}
                                </span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}

function SuccessCard({ submitted, onReset }) {
    return (
        <div className="border-2 border-[#9EE8B4]/40 bg-[#9EE8B4]/5 p-6" data-testid="submit-success">
            <div className="flex items-start gap-4">
                <CheckCircle size={32} weight="fill" className="text-[#9EE8B4] flex-shrink-0" />
                <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#9EE8B4]">
                        Submitted
                    </div>
                    <h2 className="font-display font-black text-xl uppercase tracking-tight mt-1">
                        {submitted.title}
                    </h2>
                    <p className="text-white/70 text-sm mt-2">
                        Thanks, <span className="text-fa-yellow">{submitted.submitterName}</span>!
                        Your challenge is now in moderator review. You'll see it in the Library
                        under the <span className="text-fa-yellow">Community</span> filter once
                        it's approved.
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                        <Button
                            type="button"
                            onClick={onReset}
                            data-testid="submit-another-btn"
                            className="bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-sm uppercase"
                        >
                            <UsersThree size={14} weight="bold" className="mr-2" />
                            Submit another
                        </Button>
                        <span className="font-mono text-[11px] text-white/40">
                            ID: {submitted.id}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
